import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  Card,
  Table,
  Button,
  Tag,
  Statistic,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Tooltip,
  Badge,
  Empty,
  Switch,
  Divider,
  Alert,
} from "antd";
import {
  BellOutlined,
  DollarOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../context/AuthProvider";
import {
  getLateFeeRules,
  updateLateFeeRules,
  createReminder,
  getPendingReminders,
  markReminderSent,
} from "../lib/firebase/reminderModel";
import SettingsModel from "../lib/firebase/settingsModel";
import { propertyKeys } from "../lib/queryKeys";
import { device } from "../lib/breakpoints";

const { Option } = Select;
const { TextArea } = Input;

const ReminderSettings = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leaseNotifSaving, setLeaseNotifSaving] = useState(false);
  const [lateFeeRules, setLateFeeRules] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { data: userSettings, isLoading: settingsLoading } = useQuery({
    queryKey: propertyKeys.settings(user?.uid),
    queryFn: () => SettingsModel.getByUserId(user.uid),
    enabled: !!user?.uid,
  });

  const leaseExpMasterOn =
    userSettings?.notifications?.leaseExpirations !== false;
  const alert90 = userSettings?.notifications?.leaseExpirationAlert90 !== false;
  const alert60 = userSettings?.notifications?.leaseExpirationAlert60 !== false;
  const alert30 = userSettings?.notifications?.leaseExpirationAlert30 !== false;

  const saveLeaseNotificationKey = async (key, value) => {
    if (!user?.uid || !userSettings) {
      message.warning("Settings not loaded yet.");
      return;
    }
    try {
      setLeaseNotifSaving(true);
      const notifications = {
        ...(userSettings.notifications || {}),
        [key]: value,
      };
      await SettingsModel.updateSettings(user.uid, { notifications });
      queryClient.setQueryData(propertyKeys.settings(user.uid), {
        ...userSettings,
        notifications,
      });
      message.success("Saved");
    } catch (err) {
      console.error(err);
      message.error("Could not save notification preference");
    } finally {
      setLeaseNotifSaving(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rules, pendingReminders] = await Promise.all([
        getLateFeeRules(),
        getPendingReminders(new Date()),
      ]);

      setLateFeeRules(rules);
      setReminders(pendingReminders);
    } catch (error) {
      console.error("Error loading reminder data:", error);
      message.error("Failed to load reminder settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRules = async (values) => {
    try {
      setSaving(true);
      await updateLateFeeRules(values);
      setLateFeeRules(values);
      message.success("Late fee rules updated successfully!");
    } catch (error) {
      console.error("Error updating late fee rules:", error);
      message.error("Failed to update late fee rules");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReminder = async (values) => {
    try {
      setSaving(true);
      await createReminder({
        ...values,
        scheduledDate: values.scheduledDate?.toDate?.() || values.scheduledDate,
      });

      setIsModalVisible(false);
      form.resetFields();
      message.success("Reminder created successfully!");
      loadData();
    } catch (error) {
      console.error("Error creating reminder:", error);
      message.error("Failed to create reminder");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => {
        const typeConfig = {
          rent_reminder: { color: "blue", text: "Rent Reminder" },
          late_fee: { color: "red", text: "Late Fee" },
          lease_renewal: { color: "green", text: "Lease Renewal" },
        };
        const config = typeConfig[type] || { color: "default", text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Tenant",
      dataIndex: "tenantId",
      key: "tenantId",
      render: (tenantId) => `Tenant ${tenantId}`, // TODO: Get tenant name
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (channel) => {
        const channelConfig = {
          email: { icon: "📧", text: "Email" },
          sms: { icon: "📱", text: "SMS" },
          both: { icon: "📧📱", text: "Both" },
        };
        const config = channelConfig[channel] || { icon: "", text: channel };
        return (
          <span>
            {config.icon} {config.text}
          </span>
        );
      },
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      render: (date) => {
        if (!date) return "N/A";
        const scheduledDate = date?.toDate?.() || new Date(date);
        return scheduledDate.toLocaleDateString();
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          pending: { color: "processing", icon: <ClockCircleOutlined /> },
          sent: { color: "success", icon: <CheckCircleOutlined /> },
          failed: { color: "error", icon: <ExclamationCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: "default", icon: null };
        return (
          <Tag color={config.color} icon={config.icon}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button.Group size="small">
          <Tooltip title="View Details">
            <Button icon={<EyeOutlined />} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button icon={<DeleteOutlined />} danger />
          </Tooltip>
        </Button.Group>
      ),
    },
  ];

  return (
    <Container className="reminder-settings-container">
      <Header>
        <HeaderContent>
          <div className="header-text-container">
            <Title>
              <BellOutlined style={{ marginRight: "12px", fontSize: "2rem" }} />
              Reminder Settings
            </Title>
            <Subtitle>
              Configure automated reminders and late fee rules
            </Subtitle>
          </div>
        </HeaderContent>
      </Header>

      {/* Lease expiration → Leo renewal emails (cron: 90 / 60 / 30 days) */}
      <Card
        title={
          <span>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Lease expiration alerts
          </span>
        }
        className="section-card"
        style={{ marginBottom: "2rem" }}
        loading={settingsLoading}
      >
        {!settingsLoading && !userSettings && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Open Settings once to create your notification preferences, then return here to tune lease alerts."
          />
        )}
        <p style={{ marginBottom: 16, color: "var(--color-text-light)" }}>
          Daily emails when a lease ends in exactly 90, 60, or 30 days. Each
          message includes a link to open <strong>Leo AI</strong> with a renewal
          prompt for that lease. Turn off all lease emails under{" "}
          <Link to="/settings#notifications">
            Settings → Notifications → Lease expiration reminders
          </Link>
          .
        </p>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span>90 days before end</span>
              <Switch
                checked={leaseExpMasterOn && alert90}
                disabled={!leaseExpMasterOn || leaseNotifSaving}
                loading={leaseNotifSaving}
                onChange={(v) =>
                  saveLeaseNotificationKey("leaseExpirationAlert90", v)
                }
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span>60 days before end</span>
              <Switch
                checked={leaseExpMasterOn && alert60}
                disabled={!leaseExpMasterOn || leaseNotifSaving}
                loading={leaseNotifSaving}
                onChange={(v) =>
                  saveLeaseNotificationKey("leaseExpirationAlert60", v)
                }
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span>30 days before end</span>
              <Switch
                checked={leaseExpMasterOn && alert30}
                disabled={!leaseExpMasterOn || leaseNotifSaving}
                loading={leaseNotifSaving}
                onChange={(v) =>
                  saveLeaseNotificationKey("leaseExpirationAlert30", v)
                }
              />
            </div>
          </Col>
        </Row>
        {!leaseExpMasterOn && (
          <p style={{ marginTop: 12, marginBottom: 0, color: "#fa8c16" }}>
            Master &quot;Lease expiration reminders&quot; is off in Settings -
            enable it to receive these alerts.
          </p>
        )}
      </Card>

      {/* Late Fee Rules */}
      <Card
        title="Late Fee Rules"
        className="section-card"
        style={{ marginBottom: "2rem" }}
      >
        <Form
          layout="vertical"
          initialValues={lateFeeRules}
          onFinish={handleSaveRules}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="gracePeriod"
                label="Grace Period (days)"
                rules={[
                  { required: true, message: "Please enter grace period!" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={30}
                  style={{ width: "100%" }}
                  placeholder="3"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="flatFee"
                label="Flat Late Fee ($)"
                rules={[{ required: true, message: "Please enter flat fee!" }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="50"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="percentageFee"
                label="Percentage Fee (%)"
                rules={[
                  { required: true, message: "Please enter percentage fee!" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={20}
                  style={{ width: "100%" }}
                  placeholder="5"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="maxLateFee"
                label="Maximum Late Fee ($)"
                rules={[
                  { required: true, message: "Please enter maximum fee!" },
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="200"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="compounding"
                label="Compound Late Fees"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="autoApply"
                label="Auto-Apply Late Fees"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            icon={<SettingOutlined />}
          >
            Save Late Fee Rules
          </Button>
        </Form>
      </Card>

      {/* Reminders Overview */}
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24} sm={8}>
          <StatCard>
            <Statistic
              title="Pending Reminders"
              value={reminders.filter((r) => r.status === "pending").length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatCard>
            <Statistic
              title="Sent Today"
              value={reminders.filter((r) => r.status === "sent").length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={8}>
          <StatCard>
            <Statistic
              title="Failed Reminders"
              value={reminders.filter((r) => r.status === "failed").length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </StatCard>
        </Col>
      </Row>

      {/* Reminders Table */}
      <Card
        title="Scheduled Reminders"
        className="section-card"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Create Reminder
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={reminders}
          loading={loading}
          rowKey="id"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No reminders scheduled"
              />
            ),
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} reminders`,
          }}
        />
      </Card>

      {/* Create Reminder Modal */}
      <Modal
        title="Create New Reminder"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => form.submit()}
            loading={saving}
          >
            Create Reminder
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateReminder}>
          <Form.Item
            name="type"
            label="Reminder Type"
            rules={[
              { required: true, message: "Please select reminder type!" },
            ]}
          >
            <Select placeholder="Select reminder type">
              <Option value="rent_reminder">Rent Reminder</Option>
              <Option value="late_fee">Late Fee Notice</Option>
              <Option value="lease_renewal">Lease Renewal</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="tenantId"
            label="Tenant"
            rules={[{ required: true, message: "Please select tenant!" }]}
          >
            <Select placeholder="Select tenant">
              <Option value="tenant_1">John Smith - Unit 101</Option>
              <Option value="tenant_2">Sarah Johnson - Unit 102</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="channel"
            label="Channel"
            rules={[{ required: true, message: "Please select channel!" }]}
          >
            <Select placeholder="Select channel">
              <Option value="email">Email</Option>
              <Option value="sms">SMS</Option>
              <Option value="both">Both</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduledDate"
            label="Scheduled Date"
            rules={[
              { required: true, message: "Please select scheduled date!" },
            ]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              placeholder="Select date and time"
              format="MM/DD/YYYY HH:mm"
              picker="date"
              showToday={true}
              allowClear={true}
            />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: "Please enter message!" }]}
          >
            <TextArea rows={4} placeholder="Enter reminder message..." />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
};

export default ReminderSettings;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 2rem;

  @media ${device.mobile} {
    padding: 1rem;
  }

  @media ${device.tablet} {
    padding: 1.5rem;
  }

  &.reminder-settings-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  }
`;

const Header = styled.div`
  background: var(--gradient-primary);
  border-radius: 20px;
  padding: 1.25rem 2rem;
  margin-bottom: 1.5rem;
  color: white;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-text-container {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-family: var(--font-primary);
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 1200px) {
    font-size: 2.125rem;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  margin: 0;
  opacity: 0.95;
  color: white;
  font-weight: 500;
  letter-spacing: -0.01em;
  position: relative;
  z-index: 1;
  margin-top: 0.75rem;
`;

const StatCard = styled(Card)`
  text-align: center;
  border-radius: 16px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 4px 20px rgba(44, 95, 45, 0.08);
  background: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);
  }

  .ant-statistic-title {
    font-size: 0.9rem;
    color: var(--color-text-light);
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-text) !important;
  }
`;
